/* ETHERLIGHTS – Produktwelten. Hinter der Produktseite liegt die Welt der Lampe ([data-eth-world]):
   facettierte Landschafts-Ebenen im Kristall-Look (einmal gezeichnet, per Scroll-Parallaxe bewegt) und eine animierte
   Effekt-Ebene (Glut, Blasen, Sterne, Schnee, Glühwürmchen …) mit inszenierten Figuren (Drache, Fischschwärme, Fledermäuse).
   Welten: feuer, drache, ozean, kosmos, urzeit, winter, grusel, heilig. Optional liegen eigene Bilder (z. B. aus Higgsfield)
   als .world__img-Ebenen darüber; sie bewegen sich mit derselben Parallaxe. */
(() => {
  const root = document.querySelector("[data-eth-world]");
  if (!root) return;
  const world = root.dataset.ethWorld;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const coarse = matchMedia("(pointer: coarse)").matches || innerWidth < 760;
  const DPR = Math.min(devicePixelRatio || 1, coarse ? 1.5 : 2);
  const FXDPR = Math.min(DPR, 1.25); // Effekt-Ebene ist weich – volle Retina-Auflösung bringt hier nichts

  const rng = (seed) => () => { seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  const rgba = (h, a) => { const n = parseInt(h.slice(1), 16); return `rgba(${n >> 16 & 255},${n >> 8 & 255},${n & 255},${a})`; };
  // Vorgerenderte Leuchtpunkte (shadowBlur pro Partikel und Frame ist auf Retina-Displays sehr teuer)
  const sprites = {};
  const sprite = (core, halo) => {
    const k = core + halo; if (sprites[k]) return sprites[k];
    const c = document.createElement("canvas"); c.width = c.height = 64; const g = c.getContext("2d");
    const rg = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    rg.addColorStop(0, core); rg.addColorStop(0.18, core); rg.addColorStop(0.3, rgba(halo, 0.55)); rg.addColorStop(1, rgba(halo, 0));
    g.fillStyle = rg; g.fillRect(0, 0, 64, 64); return (sprites[k] = c);
  };
  const dot = (x, img, px, py, r, a) => { if (a <= 0.01) return; const d = r * 7; x.globalAlpha = Math.min(1, a); x.drawImage(img, px - d / 2, py - d / 2, d, d); };

  /* ---------- Welt-Definitionen ---------- */
  const W = {
    feuer:  { sky: ["#1a0503", "#3a0d06", "#0a0306"], rim: "#ff8a3d", glow: "#ff5a1f", ground: ["#2a0c07", "#0b0405"], fx: "embers", figure: "dragon" },
    drache: { sky: ["#0c0a1f", "#1b1640", "#05050d"], rim: "#b493ff", glow: "#7f6bff", ground: ["#151230", "#06050f"], fx: "mist", figure: "dragon" },
    ozean:  { sky: ["#021b2c", "#04304a", "#010a14"], rim: "#7fe3ff", glow: "#2fb7ff", ground: ["#05253a", "#020a12"], fx: "bubbles", figure: "fish" },
    kosmos: { sky: ["#05031a", "#140b3a", "#020109"], rim: "#b493ff", glow: "#6a5bff", ground: ["#1a1830", "#08070f"], fx: "stars", figure: "comet" },
    urzeit: { sky: ["#04140d", "#0d2a1b", "#020805"], rim: "#5ff2a6", glow: "#ff9a4d", ground: ["#0b2416", "#030a06"], fx: "fireflies", figure: "pterosaur" },
    winter: { sky: ["#06122a", "#122a4f", "#030814"], rim: "#d8f6ff", glow: "#7fe3ff", ground: ["#d9ecff", "#6f8fb5"], fx: "snow", figure: "none" },
    grusel: { sky: ["#0d0618", "#24103a", "#05030a"], rim: "#ff9a3d", glow: "#b493ff", ground: ["#140a1f", "#050308"], fx: "wisps", figure: "bats" },
    heilig: { sky: ["#0a0820", "#1e1638", "#060410"], rim: "#ffd27a", glow: "#ffb84d", ground: ["#2a1d12", "#0c0805"], fx: "sparkles", figure: "none" },
  };
  const C = W[world] || W.drache;

  /* ---------- Ebenen (statisch gezeichnet, Parallaxe per transform) ---------- */
  const stage = root.querySelector(".world__layers");
  const fx = root.querySelector(".world__fx");
  const fxx = fx.getContext("2d");
  const layers = []; // { el, speed, draw(ctx, w, h, R) }
  let vw = 0, vh = 0;

  function facetRidge(x, w, h, base, amp, steps, R, fillTop, fillBot, rim, rimA) {
    const bottom = h * 1.7; // Ebenen sind höher als der Bildschirm, damit beim Hochschieben kein Spalt entsteht
    const pts = [[-20, bottom]];
    for (let i = 0; i <= steps; i++) pts.push([i / steps * (w + 40) - 20, base - amp * (0.35 + 0.65 * R())]);
    pts.push([w + 20, bottom]);
    const g = x.createLinearGradient(0, base - amp, 0, bottom);
    g.addColorStop(0, fillTop); g.addColorStop(1, fillBot);
    x.beginPath(); pts.forEach(([a, b], i) => i ? x.lineTo(a, b) : x.moveTo(a, b)); x.closePath(); x.fillStyle = g; x.fill();
    // Facetten: jedes Segment als helleres / dunkleres Dreieck -> Kristall- bzw. Acryl-Look
    for (let i = 1; i < pts.length - 2; i++) {
      const [ax, ay] = pts[i], [bx, by] = pts[i + 1], mx = (ax + bx) / 2, my = Math.max(ay, by) + (h - base) * (0.25 + 0.4 * R());
      x.beginPath(); x.moveTo(ax, ay); x.lineTo(bx, by); x.lineTo(mx, my); x.closePath();
      x.fillStyle = R() < 0.5 ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.18)"; x.fill();
    }
    x.save(); x.beginPath(); pts.slice(1, -1).forEach(([a, b], i) => i ? x.lineTo(a, b) : x.moveTo(a, b));
    x.strokeStyle = rgba(rim, rimA); x.lineWidth = 1.4; x.shadowColor = rim; x.shadowBlur = 14; x.stroke(); x.restore();
    return pts;
  }
  function volcano(x, cx, base, w, h, R) {
    const top = base - h, cw = w * 0.12;
    const g = x.createLinearGradient(0, top, 0, base); g.addColorStop(0, "#2a0b06"); g.addColorStop(1, "#0d0405");
    x.beginPath(); x.moveTo(cx - w / 2, base); x.lineTo(cx - cw, top); x.lineTo(cx - cw * 0.3, top + h * 0.04); x.lineTo(cx + cw * 0.4, top + h * 0.02); x.lineTo(cx + cw, top + h * 0.01); x.lineTo(cx + w / 2, base); x.closePath();
    x.fillStyle = g; x.fill();
    x.globalCompositeOperation = "lighter";
    for (let i = 0; i < 7; i++) { // Lavaströme
      let px = cx + (R() - 0.5) * cw * 1.6, py = top + 6; x.beginPath(); x.moveTo(px, py);
      for (let k = 0; k < 8; k++) { px += (R() - 0.5) * 26 + (px < cx ? -8 : 8); py += h / 9; x.lineTo(px, py); }
      const lg = x.createLinearGradient(0, top, 0, base); lg.addColorStop(0, "rgba(255,190,90,.9)"); lg.addColorStop(0.6, "rgba(255,80,20,.35)"); lg.addColorStop(1, "rgba(255,60,10,0)");
      x.strokeStyle = lg; x.lineWidth = 1.5 + R() * 2.5; x.shadowColor = "#ff5a1f"; x.shadowBlur = 12; x.stroke();
    }
    x.shadowBlur = 0; x.globalCompositeOperation = "source-over";
    return { x: cx, y: top };
  }
  function castle(x, cx, base, s, rim) {
    x.fillStyle = "#0b0918";
    const tower = (tx, tw, th) => { x.fillRect(tx - tw / 2, base - th, tw, th); x.beginPath(); x.moveTo(tx - tw * 0.65, base - th); x.lineTo(tx, base - th - tw * 1.3); x.lineTo(tx + tw * 0.65, base - th); x.fill(); };
    x.fillRect(cx - 70 * s, base - 60 * s, 140 * s, 60 * s);
    for (let i = -3; i <= 3; i++) x.fillRect(cx + i * 20 * s - 6 * s, base - 70 * s, 12 * s, 10 * s);
    tower(cx - 80 * s, 28 * s, 120 * s); tower(cx + 80 * s, 28 * s, 110 * s); tower(cx, 34 * s, 170 * s);
    x.fillStyle = rgba(rim, 0.85); x.shadowColor = rim; x.shadowBlur = 10;
    [[-80, 90], [80, 80], [0, 130], [0, 100], [-30, 40], [30, 40]].forEach(([dx, dy]) => x.fillRect(cx + dx * s - 2 * s, base - dy * s, 4 * s, 7 * s));
    x.shadowBlur = 0;
  }
  function pines(x, w, base, n, R, snow) {
    for (let i = 0; i < n; i++) {
      const px = R() * w, ph = 60 + R() * 120, pw = ph * 0.42, py = base + R() * 30;
      for (let k = 0; k < 3; k++) {
        const ty = py - ph * (k * 0.28), tw = pw * (1 - k * 0.22);
        x.beginPath(); x.moveTo(px - tw / 2, ty); x.lineTo(px, ty - ph * 0.5); x.lineTo(px + tw / 2, ty); x.closePath();
        x.fillStyle = snow ? "#1d3557" : "#0a1f14"; x.fill();
        if (snow) { x.beginPath(); x.moveTo(px - tw * 0.22, ty - ph * 0.28); x.lineTo(px, ty - ph * 0.5); x.lineTo(px + tw * 0.22, ty - ph * 0.28); x.closePath(); x.fillStyle = "rgba(235,245,255,.9)"; x.fill(); }
      }
    }
  }
  function flowers(x, pts, R, col) {
    x.globalCompositeOperation = "lighter";
    for (let i = 0; i < 40; i++) {
      const p = pts[1 + ((R() * (pts.length - 3)) | 0)], fxp = p[0] + (R() - 0.5) * 60, fy = p[1] + 8 + R() * 50, r = 2 + R() * 3.5;
      x.strokeStyle = "rgba(40,90,40,.6)"; x.lineWidth = 1; x.beginPath(); x.moveTo(fxp, fy); x.lineTo(fxp + (R() - 0.5) * 6, fy + 14); x.stroke();
      for (let k = 0; k < 5; k++) { const a = k / 5 * 6.283; x.beginPath(); x.arc(fxp + Math.cos(a) * r, fy + Math.sin(a) * r, r * 0.7, 0, 7); x.fillStyle = rgba(col, 0.55); x.fill(); }
      x.beginPath(); x.arc(fxp, fy, r * 0.5, 0, 7); x.fillStyle = "rgba(255,240,200,.9)"; x.fill();
    }
    x.globalCompositeOperation = "source-over";
  }
  function coral(x, w, base, R, cols) {
    x.globalCompositeOperation = "lighter";
    for (let i = 0; i < 26; i++) {
      const cx = R() * w, cy = base + R() * 40, col = cols[(R() * cols.length) | 0];
      const branch = (bx, by, ang, len, d) => {
        const ex = bx + Math.cos(ang) * len, ey = by + Math.sin(ang) * len;
        x.beginPath(); x.moveTo(bx, by); x.lineTo(ex, ey); x.strokeStyle = rgba(col, 0.55); x.lineWidth = d * 1.6; x.lineCap = "round"; x.stroke();
        if (d > 1) { branch(ex, ey, ang - 0.45 - R() * 0.3, len * 0.72, d - 1); branch(ex, ey, ang + 0.45 + R() * 0.3, len * 0.72, d - 1); }
      };
      branch(cx, cy, -Math.PI / 2 + (R() - 0.5) * 0.4, 18 + R() * 22, 4);
    }
    x.globalCompositeOperation = "source-over";
  }
  function deadTrees(x, w, base, R) {
    x.strokeStyle = "#07040b"; x.lineCap = "round";
    const br = (bx, by, ang, len, wd, d) => { const ex = bx + Math.cos(ang) * len, ey = by + Math.sin(ang) * len; x.lineWidth = wd; x.beginPath(); x.moveTo(bx, by); x.lineTo(ex, ey); x.stroke(); if (d > 0) { br(ex, ey, ang - 0.5 + (R() - 0.5) * 0.4, len * 0.7, wd * 0.62, d - 1); br(ex, ey, ang + 0.4 + (R() - 0.5) * 0.4, len * 0.66, wd * 0.62, d - 1); } };
    [0.1, 0.82, 0.93].forEach((f) => br(w * f, base + 20, -Math.PI / 2 + (R() - 0.5) * 0.3, 90 + R() * 50, 14, 5));
  }
  function pumpkins(x, w, base, R) {
    for (let i = 0; i < 9; i++) {
      const px = w * (0.05 + R() * 0.9), py = base + 10 + R() * 30, r = 12 + R() * 14;
      const g = x.createRadialGradient(px, py, 0, px, py, r * 3.2); g.addColorStop(0, "rgba(255,150,40,.35)"); g.addColorStop(1, "rgba(255,120,30,0)");
      x.fillStyle = g; x.beginPath(); x.arc(px, py, r * 3.2, 0, 7); x.fill();
      x.fillStyle = "#e0701c"; x.beginPath(); x.ellipse(px, py, r * 1.2, r, 0, 0, 7); x.fill();
      x.fillStyle = "#ffd36b"; x.beginPath(); x.moveTo(px - r * 0.5, py - r * 0.2); x.lineTo(px - r * 0.25, py - r * 0.5); x.lineTo(px - r * 0.05, py - r * 0.2); x.fill();
      x.beginPath(); x.moveTo(px + r * 0.05, py - r * 0.2); x.lineTo(px + r * 0.25, py - r * 0.5); x.lineTo(px + r * 0.5, py - r * 0.2); x.fill();
      x.beginPath(); x.moveTo(px - r * 0.55, py + r * 0.2); x.quadraticCurveTo(px, py + r * 0.65, px + r * 0.55, py + r * 0.2); x.quadraticCurveTo(px, py + r * 0.35, px - r * 0.55, py + r * 0.2); x.fill();
    }
  }
  function ferns(x, w, base, R) {
    for (let i = 0; i < 30; i++) {
      const fxp = R() * w, fy = base + 20 + R() * 40, len = 50 + R() * 70, ang = -Math.PI / 2 + (R() - 0.5) * 1.4;
      x.strokeStyle = "rgba(20,70,40,.95)"; x.lineWidth = 2; x.beginPath(); x.moveTo(fxp, fy);
      const ex = fxp + Math.cos(ang) * len, ey = fy + Math.sin(ang) * len; x.quadraticCurveTo(fxp + Math.cos(ang) * len * 0.5 + 12, fy + Math.sin(ang) * len * 0.5, ex, ey); x.stroke();
      for (let k = 1; k < 9; k++) { const t = k / 9, px = fxp + (ex - fxp) * t, py = fy + (ey - fy) * t; x.beginPath(); x.moveTo(px, py); x.lineTo(px + Math.cos(ang + 1.2) * 16 * (1 - t), py + Math.sin(ang + 1.2) * 16 * (1 - t)); x.moveTo(px, py); x.lineTo(px + Math.cos(ang - 1.2) * 16 * (1 - t), py + Math.sin(ang - 1.2) * 16 * (1 - t)); x.stroke(); }
    }
  }
  function dunesPalms(x, w, base, R) {
    [0.12, 0.2, 0.86].forEach((f) => {
      const px = w * f, py = base + 10, hgt = 110 + R() * 40;
      x.strokeStyle = "#0b0703"; x.lineWidth = 6; x.beginPath(); x.moveTo(px, py); x.quadraticCurveTo(px + 14, py - hgt * 0.5, px + 6, py - hgt); x.stroke();
      x.fillStyle = "#0b0703";
      for (let k = 0; k < 7; k++) { const a = -Math.PI / 2 + (k - 3) * 0.5; x.beginPath(); x.moveTo(px + 6, py - hgt); x.quadraticCurveTo(px + 6 + Math.cos(a) * 40, py - hgt + Math.sin(a) * 20 - 10, px + 6 + Math.cos(a) * 70, py - hgt + Math.sin(a) * 30 + 30); x.quadraticCurveTo(px + 6 + Math.cos(a) * 40, py - hgt + Math.sin(a) * 20, px + 6, py - hgt); x.fill(); }
    });
  }
  function planet(x, cx, cy, r, c1, c2, ring) {
    const g = x.createRadialGradient(cx - r * 0.4, cy - r * 0.4, r * 0.1, cx, cy, r); g.addColorStop(0, c1); g.addColorStop(1, c2);
    x.fillStyle = g; x.beginPath(); x.arc(cx, cy, r, 0, 7); x.fill();
    const gl = x.createRadialGradient(cx, cy, r, cx, cy, r * 1.6); gl.addColorStop(0, rgba(c1, 0.25)); gl.addColorStop(1, rgba(c1, 0)); x.fillStyle = gl; x.beginPath(); x.arc(cx, cy, r * 1.6, 0, 7); x.fill();
    if (ring) { x.save(); x.translate(cx, cy); x.rotate(-0.35); x.scale(1, 0.28); x.strokeStyle = rgba(c1, 0.6); x.lineWidth = 6; x.beginPath(); x.arc(0, 0, r * 1.7, 0, 7); x.stroke(); x.lineWidth = 2; x.beginPath(); x.arc(0, 0, r * 2, 0, 7); x.stroke(); x.restore(); }
  }
  function craters(x, w, base, R) {
    for (let i = 0; i < 14; i++) { const cx = R() * w, cy = base + 30 + R() * 80, r = 10 + R() * 30; x.strokeStyle = "rgba(180,190,230,.18)"; x.lineWidth = 2; x.beginPath(); x.ellipse(cx, cy, r, r * 0.3, 0, 0, 7); x.stroke(); }
  }

  const hot = []; // Glutpunkte (z. B. Vulkankrater) in Ebenen-Koordinaten, für die Effekt-Ebene
  const SCENES = {
    feuer: [
      { speed: 0.12, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.62, h * 0.2, 9, R, "#3a0f08", "#120505", C.rim, 0.35); } },
      { speed: 0.25, draw: (x, w, h, R) => { const c = volcano(x, w * 0.72, h * 0.78, w * 0.55, h * 0.42, R); hot.push({ layer: 1, x: c.x, y: c.y, r: 120 }); } },
      { speed: 0.45, draw: (x, w, h, R) => { const p = facetRidge(x, w, h, h * 0.86, h * 0.1, 14, R, "#2a0c07", "#070203", C.rim, 0.55); flowers(x, p, R, "#ff6a8a"); } },
    ],
    drache: [
      { speed: 0.1, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.6, h * 0.24, 8, R, "#231d4a", "#0b0920", C.rim, 0.3) },
      { speed: 0.22, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.74, h * 0.14, 11, R, "#16123a", "#07061a", C.rim, 0.45); castle(x, w * 0.28, h * 0.66, Math.max(0.6, w / 1800), "#ffd27a"); } },
      { speed: 0.42, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.88, h * 0.08, 16, R, "#0f0c26", "#040309", C.rim, 0.6) },
    ],
    ozean: [
      { speed: 0.1, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.7, h * 0.16, 10, R, "#06405f", "#031726", C.rim, 0.25) },
      { speed: 0.24, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.8, h * 0.1, 13, R, "#05324d", "#020d17", C.rim, 0.4); coral(x, w, h * 0.78, R, ["#ff7aa8", "#ffb35c", "#7fe3ff", "#b493ff"]); } },
      { speed: 0.45, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.9, h * 0.06, 16, R, "#042436", "#01070d", C.rim, 0.55) },
    ],
    kosmos: [
      { speed: 0.06, draw: (x, w, h) => { planet(x, w * 0.78, h * 0.26, Math.min(w, h) * 0.12, "#b493ff", "#1a1040", true); planet(x, w * 0.18, h * 0.16, Math.min(w, h) * 0.035, "#7fe3ff", "#0b2340", false); } },
      { speed: 0.2, draw: (x, w, h) => planet(x, w * 0.3, h * 0.44, Math.min(w, h) * 0.06, "#ffd27a", "#3a2310", false) },
      { speed: 0.42, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.86, h * 0.07, 14, R, "#3a3858", "#0c0b16", C.rim, 0.5); craters(x, w, h * 0.86, R); } },
    ],
    urzeit: [
      { speed: 0.1, draw: (x, w, h, R) => { const c = volcano(x, w * 0.3, h * 0.66, w * 0.34, h * 0.26, R); hot.push({ layer: 0, x: c.x, y: c.y, r: 70 }); facetRidge(x, w, h, h * 0.68, h * 0.12, 10, R, "#123a24", "#05130b", C.rim, 0.3); } },
      { speed: 0.24, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.8, h * 0.12, 12, R, "#0e2e1c", "#040e08", C.rim, 0.45) },
      { speed: 0.45, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.9, h * 0.06, 16, R, "#0a2214", "#020604", C.rim, 0.5); ferns(x, w, h * 0.86, R); } },
    ],
    winter: [
      { speed: 0.08, draw: (x, w, h) => planet(x, w * 0.74, h * 0.2, Math.min(w, h) * 0.07, "#ffffff", "#9fb8d8", false) },
      { speed: 0.2, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.68, h * 0.18, 9, R, "#c9dcf5", "#5d7ca6", "#ffffff", 0.5); pines(x, w, h * 0.74, 18, R, true); } },
      { speed: 0.42, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.88, h * 0.06, 14, R, "#e8f3ff", "#8aa6c9", "#ffffff", 0.7); pines(x, w, h * 0.92, 8, R, true); } },
    ],
    grusel: [
      { speed: 0.06, draw: (x, w, h) => planet(x, w * 0.7, h * 0.24, Math.min(w, h) * 0.13, "#fff3d6", "#d9a85c", false) },
      { speed: 0.22, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.74, h * 0.12, 11, R, "#1c0f2e", "#08040f", C.glow, 0.4); deadTrees(x, w, h * 0.72, R); } },
      { speed: 0.42, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.9, h * 0.05, 15, R, "#120a1d", "#030205", C.rim, 0.5); pumpkins(x, w, h * 0.88, R); } },
    ],
    heilig: [
      { speed: 0.1, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.72, h * 0.1, 8, R, "#3a2a1a", "#120c07", C.rim, 0.3) },
      { speed: 0.24, draw: (x, w, h, R) => { facetRidge(x, w, h, h * 0.82, h * 0.06, 10, R, "#2a1d12", "#0a0704", C.rim, 0.45); dunesPalms(x, w, h * 0.8, R); } },
      { speed: 0.42, draw: (x, w, h, R) => facetRidge(x, w, h, h * 0.92, h * 0.04, 14, R, "#1d140c", "#050302", C.rim, 0.5) },
    ],
  };
  const scene = SCENES[world] || SCENES.drache;

  scene.forEach((def) => {
    const c = document.createElement("canvas"); c.className = "world__layer"; c.setAttribute("aria-hidden", "true");
    stage.appendChild(c); layers.push({ el: c, ...def });
  });
  const imgs = [...root.querySelectorAll(".world__img")].map((el) => ({ el, speed: +el.dataset.speed || 0.3 }));

  function paint() {
    vw = innerWidth; vh = innerHeight; hot.length = 0;
    const h = Math.round(vh * 1.6);
    layers.forEach((L, i) => {
      L.el.width = vw * DPR; L.el.height = h * DPR; L.el.style.height = h + "px";
      const x = L.el.getContext("2d"); x.setTransform(DPR, 0, 0, DPR, 0, 0); x.clearRect(0, 0, vw, h);
      const before = hot.length;
      L.draw(x, vw, vh, rng(1000 + i * 77 + world.length));
      for (let k = before; k < hot.length; k++) hot[k].layer = i;
    });
    fx.width = vw * FXDPR | 0; fx.height = vh * FXDPR | 0;
  }

  /* ---------- Effekt-Ebene ---------- */
  const R = rng(42);
  const soft = C.fx === "mist" || C.fx === "wisps"; // große Nebelflecken: wenige reichen
  const N = soft ? (coarse ? 18 : 32) : reduced ? 30 : coarse ? 70 : 140;
  const parts = Array.from({ length: N }, () => spawn(true));
  function spawn(init) {
    const p = { x: Math.random(), y: init ? Math.random() : 1.05, s: 0.4 + Math.random() * 1.2, v: 0.4 + Math.random(), ph: Math.random() * 6.28, life: Math.random() };
    if (C.fx === "snow") p.y = init ? Math.random() : -0.05;
    return p;
  }
  const fish = Array.from({ length: 3 }, (_, i) => ({ y: 0.25 + i * 0.18, dir: i % 2 ? -1 : 1, off: Math.random(), n: 6 + i * 3, sp: 0.02 + i * 0.008 }));
  const bats = Array.from({ length: 7 }, () => ({ x: Math.random(), y: 0.15 + Math.random() * 0.3, sp: 0.03 + Math.random() * 0.04, ph: Math.random() * 6 }));

  function drawDragon(x, px, py, s, t, col) {
    const flap = 0.25 + 0.75 * Math.sin(t * 3.2); // Flügel heben sich weiter als sie sinken
    x.save(); x.translate(px, py); x.scale(s, s);
    x.fillStyle = "rgba(8,3,4,.92)"; x.strokeStyle = rgba(col, 0.75); x.lineWidth = 1.2 / s;
    const shape = () => {
      x.beginPath(); // Körper, Hals, Kopf, Schwanz
      x.moveTo(-60, 4); x.quadraticCurveTo(-90, 14, -120, 2); x.quadraticCurveTo(-95, 6, -60, -2);
      x.quadraticCurveTo(-10, -12, 30, -6); x.quadraticCurveTo(52, -18, 66, -16); x.lineTo(80, -12); x.lineTo(66, -8);
      x.quadraticCurveTo(50, -6, 34, 4); x.quadraticCurveTo(0, 12, -60, 4); x.closePath();
      x.moveTo(-6, -6); x.lineTo(-30, -6 - 70 * flap); x.lineTo(-58, -2 - 52 * flap); x.lineTo(-44, -4 - 20 * flap); x.lineTo(-70, -2 - 16 * flap); x.lineTo(-30, 0); x.closePath(); // Flügel hinten
      x.moveTo(14, -6); x.lineTo(-4, -8 - 86 * flap); x.lineTo(-26, -6 - 64 * flap); x.lineTo(-14, -6 - 26 * flap); x.lineTo(-36, -4 - 22 * flap); x.lineTo(0, 0); x.closePath(); // Flügel vorn
    };
    shape(); x.fill(); x.stroke(); x.strokeStyle = rgba(col, 0.18); x.lineWidth = 6 / s; x.stroke(); x.restore();
  }
  function drawPterosaur(x, px, py, s, t) {
    const f = Math.sin(t * 2.6) * 0.7; x.save(); x.translate(px, py); x.scale(s, s); x.fillStyle = "rgba(4,12,8,.9)";
    x.beginPath(); x.moveTo(-30, 0); x.lineTo(30, -2); x.lineTo(44, -6); x.lineTo(32, 2); x.lineTo(-30, 4); x.closePath();
    x.moveTo(0, 0); x.lineTo(-20, -60 * f - 8); x.lineTo(-40, -30 * f); x.closePath(); x.moveTo(6, 0); x.lineTo(10, 60 * f + 8); x.lineTo(-20, 30 * f); x.closePath(); x.fill(); x.restore();
  }

  let scrollP = 0, scrollY0 = 0, t0 = performance.now(), clock = 0;
  function onScroll() {
    scrollY0 = scrollY; const max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
    scrollP = Math.min(1, scrollY0 / max);
    layers.forEach((L) => { L.el.style.transform = `translate3d(0, ${(-scrollP * L.speed * vh * 0.9).toFixed(1)}px, 0)`; L.offset = -scrollP * L.speed * vh * 0.9; });
    imgs.forEach((I) => { I.el.style.transform = `translate3d(0, ${(-scrollP * I.speed * vh * 0.9).toFixed(1)}px, 0) scale(${1 + scrollP * 0.06})`; });
    root.style.setProperty("--wp", scrollP.toFixed(3));
    // Nach der Kaufbox bzw. dem Katalog-Kopf die Welt über eine Bildschirmhöhe hinweg weich abdunkeln, damit die folgenden Abschnitte ruhig lesbar sind
    const main = document.querySelector("[data-eth-product], [data-world-anchor]");
    if (main) { const end = main.getBoundingClientRect().bottom, f = Math.min(1, Math.max(0, (innerHeight * 0.85 - end) / innerHeight)); root.style.setProperty("--wf", (f * 0.82).toFixed(3)); }
  }

  let running = false, scrollQueued = false;
  function frame(now) {
    if (document.hidden) { running = false; return; }
    requestAnimationFrame(frame);
    if (now - t0 < 15) return; // 120-Hz-Displays: nicht doppelt so oft zeichnen
    if (scrollQueued) { scrollQueued = false; onScroll(); }
    const dt = Math.min(0.05, (now - t0) / 1000); t0 = now; if (!reduced) clock += dt;
    const x = fxx; x.setTransform(FXDPR, 0, 0, FXDPR, 0, 0); x.clearRect(0, 0, vw, vh);
    x.globalCompositeOperation = "lighter";

    // Glutpunkte (Krater) flackern; Intensität steigt beim Scrollen
    hot.forEach((h) => {
      const L = layers[h.layer]; const y = h.y + (L ? L.offset || 0 : 0); const fl = 0.75 + 0.25 * Math.sin(clock * 7 + h.x) + 0.1 * Math.sin(clock * 17);
      const r = h.r * (1 + scrollP * 0.6) * fl; const g = x.createRadialGradient(h.x, y, 0, h.x, y, r);
      g.addColorStop(0, "rgba(255,200,110,.75)"); g.addColorStop(0.35, "rgba(255,90,30,.35)"); g.addColorStop(1, "rgba(255,60,20,0)");
      x.fillStyle = g; x.beginPath(); x.arc(h.x, y, r, 0, 7); x.fill();
    });

    // Lichtstrahlen (Ozean, Bethlehem)
    if (C.fx === "bubbles" || C.fx === "sparkles") {
      for (let i = 0; i < 5; i++) {
        const bx = vw * (0.15 + i * 0.18) + Math.sin(clock * 0.3 + i) * 40, a = 0.05 + 0.03 * Math.sin(clock * 0.8 + i * 2);
        const g = x.createLinearGradient(bx, 0, bx + 120, vh); g.addColorStop(0, rgba(C.rim, a * 2)); g.addColorStop(1, rgba(C.rim, 0));
        x.fillStyle = g; x.beginPath(); x.moveTo(bx - 30, 0); x.lineTo(bx + 30, 0); x.lineTo(bx + 260, vh); x.lineTo(bx + 60, vh); x.closePath(); x.fill();
      }
    }
    if (C.fx === "sparkles") { // Stern von Bethlehem
      const sx = vw * 0.5, sy = vh * 0.16 - scrollP * vh * 0.1, pulse = 1 + 0.08 * Math.sin(clock * 2);
      const g = x.createRadialGradient(sx, sy, 0, sx, sy, 160 * pulse); g.addColorStop(0, "rgba(255,240,200,.9)"); g.addColorStop(0.2, "rgba(255,210,122,.35)"); g.addColorStop(1, "rgba(255,190,90,0)");
      x.fillStyle = g; x.beginPath(); x.arc(sx, sy, 160 * pulse, 0, 7); x.fill();
      x.strokeStyle = "rgba(255,235,190,.8)"; x.lineWidth = 2; [[0, 1], [1, 0], [0.6, 0.6], [-0.6, 0.6]].forEach(([dx, dy]) => { const l = (dx && dy ? 50 : 110) * pulse; x.beginPath(); x.moveTo(sx - dx * l, sy - dy * l); x.lineTo(sx + dx * l, sy + dy * l); x.stroke(); });
    }

    // Partikel
    for (const p of parts) {
      const px = p.x * vw, py = p.y * vh;
      switch (C.fx) {
        case "embers": {
          if (!reduced) { p.y -= p.v * dt * (0.12 + scrollP * 0.18); p.x += Math.sin(clock * 2 + p.ph) * dt * 0.02; }
          const a = Math.max(0, Math.min(1, p.y)) * (0.6 + 0.4 * Math.sin(clock * 9 + p.ph)); const r = p.s * 2.2;
          dot(x, sprite("rgba(255,170,80,1)", "#ff6a1f"), px, py, r, a);
          if (p.y < -0.05) Object.assign(p, spawn(false)); break;
        }
        case "bubbles": {
          if (!reduced) { p.y -= p.v * dt * 0.08; p.x += Math.sin(clock * 1.5 + p.ph) * dt * 0.01; }
          x.strokeStyle = rgba(C.rim, 0.35 * p.s); x.lineWidth = 1; x.beginPath(); x.arc(px, py, p.s * 4, 0, 7); x.stroke();
          x.fillStyle = "rgba(255,255,255,.35)"; x.beginPath(); x.arc(px - p.s * 1.4, py - p.s * 1.4, p.s * 0.9, 0, 7); x.fill();
          if (p.y < -0.05) Object.assign(p, spawn(false)); break;
        }
        case "stars": {
          const a = 0.3 + 0.7 * Math.abs(Math.sin(clock * (0.5 + p.v) + p.ph));
          dot(x, sprite("rgba(255,255,255,1)", C.rim), px, (p.y * vh * 0.8 - scrollP * vh * 0.05 * p.s), p.s * 1.1, a * 0.9); break;
        }
        case "snow": {
          if (!reduced) { p.y += p.v * dt * 0.06; p.x += Math.sin(clock + p.ph) * dt * 0.015; }
          x.fillStyle = `rgba(255,255,255,${0.5 + p.s * 0.3})`; x.beginPath(); x.arc(px, py, p.s * 2, 0, 7); x.fill();
          if (p.y > 1.05) Object.assign(p, spawn(false), { y: -0.05 }); break;
        }
        case "fireflies": {
          if (!reduced) { p.x += Math.sin(clock * 0.7 + p.ph) * dt * 0.02; p.y += Math.cos(clock * 0.5 + p.ph * 2) * dt * 0.015; }
          const a = Math.max(0, Math.sin(clock * 2 + p.ph * 3));
          dot(x, sprite("rgba(210,255,140,1)", "#c6f25f"), px, 0.35 * vh + p.y * vh * 0.6, p.s * 2, a); break;
        }
        case "wisps": {
          if (!reduced) { p.x += dt * 0.01 * p.v; if (p.x > 1.1) p.x = -0.1; }
          x.globalAlpha = 0.11; const d = 160 * p.s; x.drawImage(sprite(rgba(C.glow, 0.5), C.glow), px - d / 2, py - d / 2, d, d); break;
        }
        case "mist": {
          if (!reduced) { p.x += dt * 0.006 * p.v; if (p.x > 1.15) p.x = -0.15; }
          x.globalAlpha = 0.09; const d = 280 * p.s; x.drawImage(sprite(rgba(C.glow, 0.5), C.glow), px - d / 2, py - d / 2, d, d); break;
        }
        case "sparkles": {
          const a = Math.max(0, Math.sin(clock * 1.6 + p.ph * 4));
          dot(x, sprite("rgba(255,220,150,1)", C.rim), px, py * 0.85, p.s * 1.4, a * 0.9); break;
        }
      }
    }
    x.globalAlpha = 1;

    // Inszenierte Figuren: wandern abhängig von Zeit und Scroll-Position durchs Bild
    x.globalCompositeOperation = "source-over";
    if (C.figure === "dragon") {
      const u = ((clock * 0.025) + scrollP * 0.9) % 1.3 - 0.15;
      drawDragon(x, u * vw, vh * (0.32 - 0.08 * Math.sin(u * 6)), Math.max(1, vw / 1000), clock, C.rim);
    } else if (C.figure === "pterosaur") {
      const u = ((clock * 0.03) + scrollP) % 1.3 - 0.15; drawPterosaur(x, vw - u * vw, vh * 0.28, Math.max(0.5, vw / 1800), clock);
    } else if (C.figure === "fish") {
      fish.forEach((f) => { const u = ((clock * f.sp + f.off + scrollP * 0.4) % 1.4) - 0.2; for (let k = 0; k < f.n; k++) { const fxp = (f.dir > 0 ? u : 1 - u) * vw + (k % 3) * 22 * -f.dir + Math.sin(k) * 10, fy = vh * f.y + Math.sin(clock * 2 + k) * 6 + ((k / 3) | 0) * 12; x.fillStyle = "rgba(2,10,18,.75)"; x.beginPath(); x.ellipse(fxp, fy, 9, 3.4, 0, 0, 7); x.fill(); x.beginPath(); x.moveTo(fxp - 8 * f.dir, fy); x.lineTo(fxp - 14 * f.dir, fy - 4); x.lineTo(fxp - 14 * f.dir, fy + 4); x.fill(); } });
    } else if (C.figure === "bats") {
      bats.forEach((b) => { if (!reduced) b.x = (b.x + dt * b.sp) % 1.2; const bx = b.x * vw, by = vh * b.y + Math.sin(clock * 2 + b.ph) * 14, f = Math.sin(clock * 12 + b.ph); x.fillStyle = "rgba(6,3,10,.9)"; x.beginPath(); x.moveTo(bx, by); x.lineTo(bx - 14, by - 8 * f); x.lineTo(bx - 7, by + 2); x.lineTo(bx, by + 4); x.lineTo(bx + 7, by + 2); x.lineTo(bx + 14, by - 8 * f); x.closePath(); x.fill(); });
    } else if (C.figure === "comet" && !reduced) {
      const u = (clock * 0.08) % 1.6; if (u < 1) { const cx = vw * (0.1 + u * 0.8), cy = vh * (0.08 + u * 0.25); const g = x.createLinearGradient(cx - 160, cy - 50, cx, cy); g.addColorStop(0, "rgba(255,255,255,0)"); g.addColorStop(1, "rgba(255,255,255,.85)"); x.strokeStyle = g; x.lineWidth = 2; x.beginPath(); x.moveTo(cx - 160, cy - 50); x.lineTo(cx, cy); x.stroke(); }
    }
  }
  const start = () => { if (running || document.hidden) return; running = true; t0 = performance.now(); requestAnimationFrame(frame); };

  paint(); onScroll();
  let rz; addEventListener("resize", () => { clearTimeout(rz); rz = setTimeout(() => { paint(); onScroll(); }, 150); });
  addEventListener("scroll", () => { scrollQueued = true; }, { passive: true });
  document.addEventListener("visibilitychange", start);
  start();

  /* Acryl-Bühne: Produktbild neigt sich leicht mit dem Zeiger, Lichtreflex folgt */
  const card = document.querySelector("[data-eth-acrylic]");
  if (card && !coarse && !reduced) {
    card.addEventListener("pointermove", (e) => { const r = card.getBoundingClientRect(), dx = (e.clientX - r.left) / r.width - 0.5, dy = (e.clientY - r.top) / r.height - 0.5; card.style.setProperty("--rx", (-dy * 8).toFixed(2) + "deg"); card.style.setProperty("--ry", (dx * 10).toFixed(2) + "deg"); card.style.setProperty("--gx", ((dx + 0.5) * 100).toFixed(1) + "%"); card.style.setProperty("--gy", ((dy + 0.5) * 100).toFixed(1) + "%"); });
    card.addEventListener("pointerleave", () => { card.style.setProperty("--rx", "0deg"); card.style.setProperty("--ry", "0deg"); });
  }
})();
